import React, { useState, useCallback } from 'react'
import { useTable, useSortBy, useGlobalFilter, usePagination, useExpanded, useFlexLayout } from 'react-table'
import { FixedSizeList } from 'react-window'
import { AiFillCaretDown, AiFillCaretUp } from 'react-icons/ai'
import { IconContext } from 'react-icons'
import classnames from 'classnames'
import { Input, Spinner } from '@wellbeingxltd/fe-lib/components'
import { invoicesTableTitle } from 'modules/Invoices/constants'
import { IoChevronBack, IoChevronForward, IoSearch } from 'react-icons/io5'
import Button from 'components/Button'
import TableButton from './TableButton'
import './styles.scss'

function Table({
    title,
    subtitle,
    subtitleAsHtml,
    data,
    columns,
    onSearch,
    onFilter,
    onChangeFilter,
    buttonDisabled,
    buttonTitle,
    secondButtonTitle,
    onButtonClick,
    onSecondButtonClick,
    renderCustomButton,
    renderCustomHeaderRight,
    showHeader = true,
    showTitle = true,
    clientSidePagination,
    serverSidePagination,
    pageSize,
    clientSideCurrentPage,
    currentPage = 1,
    onLeftClicked = () => {},
    onRightClicked = () => {},
    canGoLeft = false,
    canGoRight = true,
    setCurrentPage,
    loadingData = false,
    sortable,
    sortableColumns,
    stickyHeader,
    emptyStateText = '',
    withReactWindow,
    withReactWindowRowHeight = 100,
    expandOnRowClick,
    renderExpandedView = () => {},
    initialSortBy,
    tableClassName,
    downloadMonthInvoices,
    downloading,
    clickHandler,
}) {
    const [inputValue, setInputValue] = useState('')

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        page,
        nextPage,
        previousPage,
        canPreviousPage,
        canNextPage,
        setGlobalFilter,
        totalColumnsWidth,
        state: { pageIndex },
    } = useTable(
        {
            columns,
            data,
            initialState: {
                sortBy: initialSortBy || [],
                ...(clientSidePagination && { pageIndex: clientSideCurrentPage || 0, pageSize: pageSize || 3 }),
            },
        },
        useGlobalFilter,
        useSortBy,
        useExpanded,
        usePagination,
        useFlexLayout
    )

    const renderClientPaginationComponent = () => (
        <div className="pagination-container">
            <IoChevronBack
                className="pagination-arrow pagination-arrow-left"
                fill={canPreviousPage ? 'var(--blue)' : 'var(--grey-med)'}
                style={{ cursor: canPreviousPage ? 'pointer' : 'default' }}
                onClick={() => {
                    if (canPreviousPage) {
                        previousPage()
                        setCurrentPage && setCurrentPage(pageIndex - 1)
                    }
                }}
            />
            <div className="pagination-current-page">{pageIndex + 1}</div>
            <IoChevronForward
                className="pagination-arrow pagination-arrow-right"
                fill={canNextPage ? 'var(--blue)' : 'var(--grey-med)'}
                style={{ cursor: canNextPage ? 'pointer' : 'default' }}
                onClick={() => {
                    if (canNextPage) {
                        nextPage()
                        setCurrentPage && setCurrentPage(pageIndex + 1)
                    }
                }}
            />
        </div>
    )

    const renderServerPaginationComponent = () => (
        <div className="pagination-container">
            <IoChevronBack
                className="pagination-arrow pagination-arrow-left"
                fill={canGoLeft ? 'var(--blue)' : 'var(--grey-med)'}
                style={{ cursor: canGoLeft ? 'pointer' : 'default' }}
                onClick={() => canGoLeft && onLeftClicked()}
            />
            <div className="pagination-current-page">{loadingData ? <Spinner color="var(--blue)" /> : currentPage}</div>
            <IoChevronForward
                className="pagination-arrow pagination-arrow-right"
                fill={canGoRight ? 'var(--blue)' : 'var(--grey-med)'}
                style={{ cursor: canGoRight ? 'pointer' : 'default' }}
                onClick={() => canGoRight && onRightClicked()}
            />
        </div>
    )

    const RenderRow = useCallback(
        ({ index, style }) => {
            const row = rows[index]
            prepareRow(row)
            return (
                <tr {...row.getRowProps({ style })}>
                    {row.cells.map((cell, index) => (
                        <td key={index} {...cell.getCellProps()}>
                            {cell.render('Cell')}
                        </td>
                    ))}
                </tr>
            )
        },
        [prepareRow, rows]
    )

    return (
        <div className="module module--withTable">
            {showTitle && (
                <div className="module-header">
                    <div className="module-header-left">
                        <div className="module-header-title-wrapper">
                            <h2>{title}</h2>
                            {subtitle && <h3>{subtitle}</h3>}
                            {subtitleAsHtml && <div style={{ marginTop: `1rem` }} dangerouslySetInnerHTML={{ __html: subtitleAsHtml }}></div>}
                        </div>
                        {onSearch && (
                            <Input
                                loading={loadingData}
                                onChange={e => {
                                    setInputValue(e.target.value)
                                    onSearch(e.target.value)
                                }}
                                renderLeftIcon={() => <IoSearch />}
                                value={inputValue}
                            />
                        )}

                        {onFilter && (
                            <Input
                                onChange={e => {
                                    setInputValue(e.target.value)
                                    setGlobalFilter(e.target.value || undefined)
                                    onChangeFilter && onChangeFilter(e.target.value)
                                }}
                                renderLeftIcon={() => <IoSearch />}
                                value={inputValue}
                            />
                        )}
                    </div>
                    {onButtonClick && <TableButton text={buttonTitle} onClick={onButtonClick} />}
                    {renderCustomButton && renderCustomButton()}
                    {onSecondButtonClick && <TableButton text={secondButtonTitle} onClick={onSecondButtonClick} />}
                    {renderCustomHeaderRight && renderCustomHeaderRight()}
                </div>
            )}
            {data.length === 0 || (onFilter && rows.length === 0) ? (
                <p className="table-emptyStateText">{emptyStateText}</p>
            ) : (
                <table
                    className={classnames('table', stickyHeader && 'table--stickyHeader', tableClassName)}
                    onClick={clickHandler}
                    {...getTableProps()}
                >
                    <thead style={showHeader ? {} : { visibility: 'collapse' }}>
                        {headerGroups.map((headerGroup, index) => (
                            <tr key={index} {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column, index) => {
                                    let shouldBeSorted = sortable && sortableColumns && sortableColumns.includes(column.Header)
                                    return (
                                        <th
                                            key={index}
                                            style={column.style}
                                            {...column.getHeaderProps(shouldBeSorted ? column.getSortByToggleProps() : undefined)}
                                        >
                                            <div className="table-header">
                                                {column.render('Header')}
                                                {shouldBeSorted && (
                                                    <span className="table-sorter">
                                                        <IconContext.Provider
                                                            value={{ color: column.isSortedDesc === false ? 'var(--color-sleep)' : 'var(--grey-med' }}
                                                        >
                                                            <AiFillCaretUp />
                                                        </IconContext.Provider>
                                                        <IconContext.Provider
                                                            value={{ color: column.isSortedDesc === true ? 'var(--color-sleep)' : 'var(--grey-med' }}
                                                        >
                                                            <AiFillCaretDown />
                                                        </IconContext.Provider>
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    )
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {withReactWindow ? (
                            <FixedSizeList height={550} itemCount={rows.length} width={totalColumnsWidth} itemSize={withReactWindowRowHeight}>
                                {RenderRow}
                            </FixedSizeList>
                        ) : (
                            (clientSidePagination ? page : rows).map((row, index) => {
                                prepareRow(row)

                                // For invoices table (Finance page)
                                // row with heading of month and button to download month invoices
                                const { startMonthSection, monthHeading, date } = row.original
                                if (title === invoicesTableTitle && startMonthSection) {
                                    return (
                                        <tr key={index} {...row.getRowProps()} className="invoicesMonthHeading">
                                            <td colSpan={6}>
                                                <span>{monthHeading}</span>
                                                <Button
                                                    className="downloadingButton"
                                                    loading={downloading === date}
                                                    onClick={() => downloadMonthInvoices(date)}
                                                >
                                                    Download all for {monthHeading}
                                                </Button>
                                            </td>
                                        </tr>
                                    )
                                }

                                return (
                                    <React.Fragment key={index}>
                                        <tr {...row.getRowProps()} onClick={() => (expandOnRowClick ? row.toggleRowExpanded(!row.isExpanded) : {})}>
                                            {row.cells.map((cell, index) => (
                                                <td key={index} {...cell.getCellProps()}>
                                                    {cell.render('Cell')}
                                                </td>
                                            ))}
                                        </tr>
                                        {expandOnRowClick && row.isExpanded && renderExpandedView(row.original)}
                                    </React.Fragment>
                                )
                            })
                        )}
                    </tbody>
                </table>
            )}
            {clientSidePagination && renderClientPaginationComponent()}
            {serverSidePagination && renderServerPaginationComponent()}
        </div>
    )
}

export default Table
